/**
 * useSourei Hook - Phase 4-2
 *
 * 【壮麗】キーワード能力を管理するカスタムフック。
 * 手札から同名カードを捨てて効果を発動する。
 *
 * 特徴:
 * - useState なし（純粋な関数のみ）
 * - 複数の効果タイプをサポート（negate_effect, grant_zankon, double_attack, etc.）
 */

import { useCallback } from 'react';
import type React from 'react';
import { getSoureiEffect, findSameNameCardInHand } from '../../engine/keywordAbilities';
import type { MonsterInstance, HandCard, CardWithId } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type { PendingMonsterTarget } from '../../types/ui';

interface UseSoureiProps {
  currentPlayer: PlayerNumber;
  p1Hand: HandCard[];
  p2Hand: HandCard[];
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;
  setP1Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP2Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP1Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP1SpReduction: React.Dispatch<React.SetStateAction<number>>;
  setP2SpReduction: React.Dispatch<React.SetStateAction<number>>;
  setPendingMonsterTarget: React.Dispatch<React.SetStateAction<PendingMonsterTarget | null>>;
}

interface UseSoureiReturn {
  activateSourei: (monsterIndex: number) => void;
}

export const useSourei = ({
  currentPlayer,
  p1Hand,
  p2Hand,
  p1Field,
  p2Field,
  addLog,
  setP1Hand,
  setP2Hand,
  setP1Field,
  setP2Field,
  setP1Graveyard,
  setP2Graveyard,
  setP1SpReduction,
  setP2SpReduction,
  setPendingMonsterTarget,
}: UseSoureiProps): UseSoureiReturn => {
  /**
   * 【壮麗】発動
   */
  const activateSourei = useCallback((monsterIndex: number): void => {
    const myHand = currentPlayer === 1 ? p1Hand : p2Hand;
    const myField = currentPlayer === 1 ? p1Field : p2Field;
    const setMyHand = currentPlayer === 1 ? setP1Hand : setP2Hand;
    const setMyField = currentPlayer === 1 ? setP1Field : setP2Field;
    const setMyGraveyard = currentPlayer === 1 ? setP1Graveyard : setP2Graveyard;
    const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;

    const monster = myField[monsterIndex];
    if (!monster) return;

    // 同名カードを手札から探す
    const sameNameCard = findSameNameCardInHand(myHand, monster.name, monster.uniqueId) as HandCard | null;
    if (!sameNameCard) {
      addLog(`【壮麗】${monster.name}: 手札に同名カードがありません`, 'info');
      return;
    }

    // 手札から捨てる
    setMyHand(prev => prev.filter(c => c.uniqueId !== sameNameCard.uniqueId));
    setMyGraveyard(prev => [...prev, sameNameCard]);
    addLog(`【壮麗】${monster.name}: 手札から同名カードを捨てて効果発動！`, 'info');

    // 効果を適用
    const effect = getSoureiEffect(monster.id);
    if (!effect) return;

    switch (effect.effectType) {
      case 'negate_effect':
        // 相手モンスター1体の効果を無効化
        setPendingMonsterTarget({
          message: '効果を無効化するモンスターを選択',
          targetPlayer: 'opponent',
          callback: (targetIndex) => {
            setOpponentField(prev => {
              const newField = [...prev];
              if (newField[targetIndex]) {
                newField[targetIndex] = { ...newField[targetIndex], effectNegated: true };
              }
              return newField;
            });
            addLog(`${monster.name}の【壮麗】効果: 相手モンスターの効果を無効化`, 'info');
          }
        });
        break;

      case 'grant_zankon':
        // 自分モンスター1体に【残魂】を付与
        setPendingMonsterTarget({
          message: '【残魂】を付与するモンスターを選択',
          targetPlayer: 'self',
          callback: (targetIndex) => {
            setMyField(prev => {
              const newField = [...prev];
              if (newField[targetIndex]) {
                newField[targetIndex] = {
                  ...newField[targetIndex],
                  keyword: (newField[targetIndex].keyword || '') + '【残魂】',
                  grantedZankon: true
                };
              }
              return newField;
            });
            addLog(`${monster.name}の【壮麗】効果: 【残魂】を付与`, 'info');
          }
        });
        break;

      case 'double_attack':
        // このターン2回攻撃可能
        setMyField(prev => {
          const newField = [...prev];
          if (newField[monsterIndex]) {
            newField[monsterIndex] = { ...newField[monsterIndex], canDoubleAttack: true, attackCount: 0 };
          }
          return newField;
        });
        addLog(`${monster.name}の【壮麗】効果: このターン2回攻撃可能`, 'info');
        break;

      case 'damage_boost':
        // 次の戦闘ダメージ1.5倍
        setMyField(prev => {
          const newField = [...prev];
          if (newField[monsterIndex]) {
            newField[monsterIndex] = { ...newField[monsterIndex], damageBoost: 1.5 };
          }
          return newField;
        });
        addLog(`${monster.name}の【壮麗】効果: 次の戦闘ダメージ1.5倍`, 'info');
        break;

      case 'sp_reduce':
        // 相手の次ターンSP増加を-1（状態として記録）
        if (currentPlayer === 1) {
          setP2SpReduction(prev => prev + 1);
        } else {
          setP1SpReduction(prev => prev + 1);
        }
        addLog(`${monster.name}の【壮麗】効果: 相手の次ターンSP増加-1`, 'info');
        break;

      case 'halve_attack':
        // 相手モンスター全体の攻撃力を半減
        setOpponentField(prev => {
          return prev.map(m => {
            if (m) {
              const baseAtk = m.attack ?? 1000;
              const newAtk = Math.floor((m.currentAttack ?? baseAtk) / 2);
              return { ...m, currentAttack: newAtk };
            }
            return m;
          });
        });
        addLog(`${monster.name}の【壮麗】効果: 相手モンスター全体の攻撃力半減`, 'info');
        break;

      default:
        addLog(`${monster.name}の【壮麗】効果: 未実装の効果タイプ`, 'info');
    }

    // 壮麗使用済みフラグを設定
    setMyField(prev => {
      const newField = [...prev];
      if (newField[monsterIndex]) {
        newField[monsterIndex] = { ...newField[monsterIndex], soureiUsedThisTurn: true };
      }
      return newField;
    });
  }, [currentPlayer, p1Hand, p2Hand, p1Field, p2Field, addLog,
      setP1Hand, setP2Hand, setP1Field, setP2Field, setP1Graveyard, setP2Graveyard,
      setPendingMonsterTarget, setP1SpReduction, setP2SpReduction]);

  return {
    activateSourei,
  };
};

export default useSourei;
